/**
 * Customer Portal Ticket Detail Page
 * View and interact with a specific support ticket
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type PortalTicket } from '@/services/api/customerPortal';
import {
  ArrowLeft,
  Clock,
  MessageSquare,
  Send,
  User,
  Bot,
  CheckCircle,
  AlertCircle,
  Star,
  XCircle,
} from 'lucide-react';

export default function CustomerPortalTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<PortalTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  useEffect(() => {
    if (ticketId) {
      loadTicket();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    if (!ticketId) return;
    try {
      const data = await customerPortalService.getTicket(ticketId);
      setTicket(data);
    } catch (error) {
      console.error('Failed to load ticket:', error);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketId || !newComment.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      await customerPortalService.addComment(ticketId, { body: newComment.trim() });
      setNewComment('');
      await loadTicket(); // Reload to get the new comment
    } catch (err: any) {
      setError(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestClose = async () => {
    if (!ticketId) return;
    try {
      await customerPortalService.requestCloseTicket(ticketId);
      setShowRatingDialog(true);
    } catch (err: any) {
      setError(err.message || 'Failed to close ticket');
    }
  };

  const handleSubmitRating = async () => {
    if (!ticketId || rating === 0) return;
    try {
      await customerPortalService.rateTicket(ticketId, rating, ratingComment);
      setShowRatingDialog(false);
      await loadTicket();
    } catch (err: any) {
      console.error('Failed to submit rating:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (['new', 'open'].includes(statusLower)) {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Open</Badge>;
    }
    if (['pending', 'waiting'].includes(statusLower)) {
      return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Pending</Badge>;
    }
    if (['in_progress'].includes(statusLower)) {
      return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">In Progress</Badge>;
    }
    if (['resolved', 'closed'].includes(statusLower)) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Resolved</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityLower = priority.toLowerCase();
    if (priorityLower === 'urgent') {
      return <Badge variant="destructive">Urgent</Badge>;
    }
    if (priorityLower === 'high') {
      return <Badge className="bg-red-100 text-red-700">High</Badge>;
    }
    if (priorityLower === 'medium') {
      return <Badge className="bg-amber-100 text-amber-700">Medium</Badge>;
    }
    return <Badge variant="outline">Low</Badge>;
  };

  const isTicketOpen = ticket && !['resolved', 'closed'].includes(ticket.status.toLowerCase());

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </CustomerPortalLayout>
    );
  }

  if (!ticket) {
    return (
      <CustomerPortalLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Ticket Not Found</h2>
          <p className="text-gray-500 mb-4">The ticket you're looking for doesn't exist or you don't have access.</p>
          <Link to="/customer-portal/tickets">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Tickets
            </Button>
          </Link>
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/customer-portal/tickets"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Tickets
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-sm font-mono text-gray-500">#{ticket.ticketNumber}</span>
              {getStatusBadge(ticket.status)}
              {getPriorityBadge(ticket.priority)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {ticket.subject}
            </h1>
          </div>

          {isTicketOpen && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Resolved
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Close this ticket?</DialogTitle>
                  <DialogDescription>
                    If your issue has been resolved, you can close this ticket. You'll have a chance to rate your experience.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {}}>Cancel</Button>
                  <Button onClick={handleRequestClose}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Yes, Close Ticket
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Conversation */}
        <div className="lg:col-span-2 space-y-6">
          {/* Original Message */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">You</p>
                    <p className="text-sm text-gray-500">
                      {new Date(ticket.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose dark:prose-invert max-w-none">
                <p className="whitespace-pre-wrap">{ticket.description || ticket.subject}</p>
              </div>
            </CardContent>
          </Card>

          {/* Comments / Replies */}
          {ticket.comments.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <MessageSquare className="w-5 h-5 mr-2" />
                Conversation
              </h3>
              {ticket.comments.filter(c => c.isPublic).map((comment) => (
                <Card key={comment.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        comment.authorType === 'customer'
                          ? 'bg-blue-100'
                          : 'bg-green-100'
                      }`}>
                        {comment.authorType === 'customer' ? (
                          <User className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Bot className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {comment.authorType === 'customer' ? 'You' : 'Support Team'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(comment.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="prose dark:prose-invert max-w-none">
                          <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                            {comment.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Add Comment Form */}
          {isTicketOpen && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Add a Reply</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddComment}>
                  <Textarea
                    placeholder="Type your message here..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="mb-4"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="bg-v2accent hover:bg-v2accent/90"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Ticket Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Status</p>
                {getStatusBadge(ticket.status)}
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Priority</p>
                {getPriorityBadge(ticket.priority)}
              </div>

              {ticket.categoryName && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
                  <p className="text-gray-900 dark:text-white">{ticket.categoryName}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Created</p>
                <p className="text-gray-900 dark:text-white flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {new Date(ticket.createdAt).toLocaleString()}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Last Updated</p>
                <p className="text-gray-900 dark:text-white flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {new Date(ticket.updatedAt).toLocaleString()}
                </p>
              </div>

              {ticket.lastResponseAt && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Last Response</p>
                  <p className="text-gray-900 dark:text-white flex items-center">
                    <MessageSquare className="w-4 h-4 mr-1 text-gray-400" />
                    {new Date(ticket.lastResponseAt).toLocaleString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                Need more help?
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                Check our knowledge base for instant answers to common questions.
              </p>
              <Link to="/customer-portal/knowledge-base">
                <Button variant="outline" size="sm" className="w-full">
                  Browse Knowledge Base
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Your Experience</DialogTitle>
            <DialogDescription>
              How satisfied were you with the support you received?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`p-2 rounded-full transition-colors ${
                    star <= rating
                      ? 'text-yellow-500'
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                >
                  <Star className={`w-8 h-8 ${star <= rating ? 'fill-current' : ''}`} />
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Any additional feedback? (optional)"
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRatingDialog(false)}>
              Skip
            </Button>
            <Button onClick={handleSubmitRating} disabled={rating === 0}>
              Submit Rating
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CustomerPortalLayout>
  );
}
